"use client";

import { keepPreviousData, QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { AddToFavoriteParams, RemoveFavoritesParams } from "@/actions/favorite.action";
import { FavoriteSvcType } from "@/components/favorites/favorite.type";
import {
  handleGetMyFavoritesAction,
  handleAddItemToFavoritesAction,
  handleRemoveFavoritesAction,
} from "@/actions/favorite.action";

import { usePathname } from "@/i18n/navigation";

const SUPPORTED_FAVORITE_SVC_TYPES = new Set<FavoriteSvcType>([
  "hotel",
  "rental",
  "spa_msg",
  "tour_act",
]);

const favoriteQueryKeys = {
  listing: (svcType: FavoriteSvcType) =>
    ["favorite-listings", svcType] as const,
};

/** ******************************
 * Invalidate favorites listing base on current page context
 * - On /favorites page: actively refetch to reflect changes immediately
 * - On other pages (search, detail, home): mark stale without triggering API refetch
 ****************************** */
const invalidateFavoriteRelated = (
  queryClient: QueryClient,
  svcType: FavoriteSvcType,
  pathname?: string | null,
) => {
  const isFavoritesPage = Boolean(pathname?.includes("/favorites"));

  if (isFavoritesPage) {
    queryClient.invalidateQueries({
      queryKey: favoriteQueryKeys.listing(svcType),
      refetchType: "active",
    });
  } else {
    queryClient.invalidateQueries({
      queryKey: favoriteQueryKeys.listing(svcType),
      refetchType: "none",
    });
  }
};

/** ******************************
 * Favorites Listing
 ****************************** */
const useFavorites = <TSvcType extends FavoriteSvcType>(
  svcType: TSvcType,
) => {
  const isSupportedSvcType = SUPPORTED_FAVORITE_SVC_TYPES.has(svcType);

  return useQuery<any>({
    queryKey: favoriteQueryKeys.listing(svcType),
    queryFn: () => handleGetMyFavoritesAction(svcType),
    enabled: isSupportedSvcType,
    staleTime: 60 * 1000,
    placeholderData: keepPreviousData,
  });
};

/** ******************************
 * Handle: Add To Favorites
 ****************************** */
const useAddToFavorites = () => {
  const queryClient = useQueryClient();
  const pathname = usePathname();

  return useMutation({
    mutationFn: (payload: AddToFavoriteParams) =>
      handleAddItemToFavoritesAction(payload),
    onSuccess: (response, payload) => {
      if (!response.success) return;
      invalidateFavoriteRelated(queryClient, payload.svcType, pathname);
    },
  });
};

/** ******************************
 * Handle: Remove Favorite
 ****************************** */
const useRemoveFavorite = () => {
  const queryClient = useQueryClient();
  const pathname = usePathname();

  return useMutation({
    mutationFn: (payload: RemoveFavoritesParams) =>
      handleRemoveFavoritesAction(payload),
    onSuccess: (response, payload) => {
      if (!response.success) return;
      invalidateFavoriteRelated(queryClient, payload.svcType, pathname);
    },
  });
};

/** ******************************
 * Export all of Favorites
 ****************************** */
export {
  useFavorites,
  useAddToFavorites,
  useRemoveFavorite,
};